import React, { useCallback, useEffect, useState } from 'react';
import { usePeer } from '@/contexts/WebRTCContext.tsx';
import { FilterType } from '../VideoFilters';
import { DefaultLayout } from './DefaultLayout';
import { PiPLayout } from './PiPLayout';

type VideoAreaProps = {
  videoSizeRatio: number;
  onSliderChange: (value: number) => void;
  openDrawer: () => void;
  currentLayout: 'default' | 'picture-in-picture';
  swapPositions?: boolean;
  onSwapPositions?: () => void;
  triggerResetConnection: boolean;
  setTriggerResetConnection: React.Dispatch<React.SetStateAction<boolean>>;
  sessionActive: boolean;
  handleSessionActive: (sessionActive: boolean) => void;
  generalDataChannel: RTCDataChannel | undefined;
  onReport?: () => void;
};

/**
 * Main video area component - orchestrates video layouts
 * Declarative pattern: delegates rendering to layout components
 */
export function VideoArea({
  videoSizeRatio,
  onSliderChange,
  currentLayout,
  swapPositions,
  onSwapPositions,
  triggerResetConnection,
  setTriggerResetConnection,
  sessionActive,
  handleSessionActive,
  generalDataChannel,
  onReport
}: VideoAreaProps) {
  const [selfFilter, setSelfFilter] = useState<string>('none');

  // Get peer control functions
  const {
    isPeerAudioMuted,
    isPeerVideoHidden,
    togglePeerAudioMute,
    togglePeerVideoHide,
    rtcConnectionState
  } = usePeer();

  // Send filter changes to remote peer via data channel
  useEffect(() => {
    if (!generalDataChannel || generalDataChannel.readyState !== 'open') return;
    generalDataChannel.send(JSON.stringify({ type: 'filter-change', payload: selfFilter }));
  }, [selfFilter, generalDataChannel]);

  // Handle filter changes
  const onFilterChange = useCallback((_filter: FilterType, _intensity: number, filterStyle: string) => {
    setSelfFilter(filterStyle);
  }, []);

  // Shared props for both layouts
  const sharedProps = {
    videoSizeRatio,
    swapPositions,
    triggerResetConnection,
    setTriggerResetConnection,
    sessionActive,
    handleSessionActive,
    selfFilter,
    isPeerAudioMuted,
    isPeerVideoHidden,
    togglePeerAudioMute,
    togglePeerVideoHide,
    onFilterChange,
    onReport,
    rtcConnectionState
  };

  // Declarative rendering based on layout type
  return currentLayout === 'picture-in-picture' ? (
    <PiPLayout
      {...sharedProps}
      onSwapPositions={onSwapPositions}
    />
  ) : (
    <DefaultLayout
      {...sharedProps}
      onSliderChange={onSliderChange}
    />
  );
}
